"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { addMonths, endOfMonth, startOfMonth } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Button } from "../button";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useBookingAvailability, getMinBookableDate } from "../../hooks/booking/useBookingAvailability";
import { useBookingSubmission, BookingSuccessData } from "../../hooks/booking/useBookingSubmission";

// Import utilities
import {
  BookingStep,
  BookingData,
  DEFAULT_MEETING_DURATION,
} from "./utils/bookingConstants";
import { isValidBookeeEmail, validateBookingData } from "./utils/bookingValidation";
import {
  getStepInfo,
  getStepLabels,
  canNavigateForward,
  canNavigateBackward,
} from "./utils/bookingStepConfig";
import { resolveInitialTimeZone } from "./utils/bookingTimezones";

// Import hooks
import { useBookingState } from "./hooks/useBookingState";
import { useBookingNavigation } from "./hooks/useBookingNavigation";
import { useEventTypeConfig } from "./hooks/useEventTypeConfig";
import { useURLSync } from "./hooks/useURLSync";
import { useSlotPreSelection } from "./hooks/useSlotPreSelection";

// Import components
import { BookingSummarySidebar } from "./steps/BookingSummarySidebar";
import { ProgressIndicator } from "./steps/ProgressIndicator";
import { EmailValidationError } from "./steps/EmailValidationError";
import { SelectPersonStep } from "./steps/SelectPersonStep";
import { SelectSlotStep } from "./steps/SelectSlotStep";
import { BookingDetailsStep } from "./steps/BookingDetailsStep";
import { SuccessStep } from "./steps/SuccessStep";

export function PublicBookingStepper() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Initialize step based on URL params
  const getInitialStep = (): BookingStep => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('eventType')) {
        return 'select-slot';
      }
      if (params.get('email') || params.get('person')) {
        return 'select-slot';
      }
    }
    return 'select-person';
  };

  // State management
  const { bookingData, updateBookingData, resetBookingData } = useBookingState();
  const { currentStep, setStep } = useBookingNavigation(getInitialStep());
  const { eventType, config, isLoading: isLoadingEventType } = useEventTypeConfig(searchParams);
  const { updateURL } = useURLSync(searchParams, bookingData, eventType);

  // Resolve the booker's display zone AFTER mount. `useBookingState` seeds the
  // business default so SSR and the first client render agree; this effect then
  // switches to `?tz=` (a rep can pin a link to the prospect's zone) or the
  // browser's own zone. Runs once — a later re-run would clobber a manual pick.
  const [timeZoneResolved, setTimeZoneResolved] = useState(false);
  useEffect(() => {
    if (timeZoneResolved) return;
    const resolved = resolveInitialTimeZone(searchParams?.get('tz'));
    setTimeZoneResolved(true);
    if (resolved !== bookingData.timeZone) {
      updateBookingData({ timeZone: resolved });
    }
    // bookingData.timeZone is read, not tracked: this must not re-run when the
    // booker changes the zone by hand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeZoneResolved, searchParams, updateBookingData]);

  const handleTimeZoneChange = useCallback(
    (zone: string) => {
      updateBookingData({ timeZone: zone });
    },
    [updateBookingData]
  );

  // Local state
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [meetingDetails, setMeetingDetails] = useState<BookingSuccessData | null>(null);

  // Calendar month window. Starts at the month of the earliest bookable date
  // (event types like sm-costsavings enforce a lead time) and is bounded so
  // bookers can't page endlessly into the future.
  const minBookableDate = useMemo(() => getMinBookableDate(eventType || undefined), [eventType]);
  const minMonth = useMemo(() => startOfMonth(minBookableDate), [minBookableDate]);
  const maxMonth = useMemo(() => addMonths(minMonth, 5), [minMonth]);
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => startOfMonth(new Date()));

  // When the event type resolves to a later minimum date, jump the calendar
  // forward so the first visible month actually has bookable days.
  useEffect(() => {
    setVisibleMonth((prev) => (prev < minMonth ? minMonth : prev));
  }, [minMonth]);

  const handleVisibleMonthChange = useCallback(
    (month: Date) => {
      const next = startOfMonth(month);
      if (next < minMonth || next > maxMonth) return;
      setVisibleMonth(next);
    },
    [minMonth, maxMonth]
  );

  const visibleMonthEnd = useMemo(() => endOfMonth(visibleMonth), [visibleMonth]);

  // Booking availability hook
  const availabilityParams = useMemo(
    () => ({
      bookeeEmail: eventType ? undefined : bookingData.bookeeEmail,
      groupEmails: eventType ? config.groupEmails : undefined,
      selectedWeek: visibleMonth,
      rangeEndDate: visibleMonthEnd,
      meetingDuration: bookingData.meetingDuration,
      availableDurations: eventType && config.availableDurations.length > 0 ? config.availableDurations : undefined,
      eventType: eventType || undefined,
      enabled: eventType ? config.groupEmails.length > 0 : !!bookingData.bookeeEmail,
    }),
    [bookingData.bookeeEmail, bookingData.meetingDuration, eventType, config.groupEmails, config.availableDurations, visibleMonth, visibleMonthEnd]
  );

  const { availability: availableDays, isLoading: isLoadingAvailability, error: availabilityError, refetch } = useBookingAvailability(availabilityParams);

  // Booking submission hook
  const { submitBooking, isLoading: isSubmitting, error: submissionError } = useBookingSubmission();

  // Update field errors when submission error changes
  useEffect(() => {
    if (submissionError?.fieldErrors) {
      setFieldErrors(submissionError.fieldErrors);
    } else if (submissionError?.message) {
      setFieldErrors({ general: submissionError.message });
    }
  }, [submissionError]);

  // Initialize from URL parameters (for non-event-type mode)
  useEffect(() => {
    const eventTypeParam = searchParams?.get('eventType');

    if (eventTypeParam) {
      return;
    }

    const emailParam = searchParams?.get('email');
    const durationParam = searchParams?.get('duration');
    const personParam = searchParams?.get('person');

    if (!emailParam && !personParam) {
      setEmailValidationError(null);
    }

    if (emailParam) {
      const decodedEmail = decodeURIComponent(emailParam);
      if (!isValidBookeeEmail(decodedEmail)) {
        setEmailValidationError(decodedEmail);
        return;
      }
      setEmailValidationError(null);
      updateBookingData({ bookeeEmail: decodedEmail });
    }

    if (personParam) {
      const decodedPerson = decodeURIComponent(personParam);
      if (!isValidBookeeEmail(decodedPerson)) {
        setEmailValidationError(decodedPerson);
        return;
      }
      setEmailValidationError(null);
      updateBookingData({ bookeeEmail: decodedPerson });
    }

    if (durationParam) {
      const duration = parseInt(durationParam, 10);
      if (!isNaN(duration)) {
        updateBookingData({ meetingDuration: duration });
      }
    }

    if (emailParam || personParam) {
      setStep('select-slot');
    } else {
      if (!eventTypeParam) {
        setStep('select-person');
      }
    }
  }, [searchParams, updateBookingData, setStep]);

  // Pre-fill name and email from URL if provided (event type mode)
  useEffect(() => {
    const eventTypeParam = searchParams?.get('eventType');
    const nameParam = searchParams?.get('name');
    const emailParam = searchParams?.get('email');

    if (eventTypeParam) {
      if (nameParam) {
        updateBookingData({ name: decodeURIComponent(nameParam) });
      }
      if (emailParam) {
        updateBookingData({ email: decodeURIComponent(emailParam) });
      }

      // Update booking data with event type config
      if (config.defaultDuration) {
        updateBookingData({ meetingDuration: config.defaultDuration });
      }
      updateBookingData({
        location: config.location || '',
        isOnline: config.isOnline !== undefined ? config.isOnline : true,
        description: config.description || bookingData.description || '',
      });

      // Skip person selection step
      setStep('select-slot');
    }
  }, [eventType, config, searchParams, updateBookingData, setStep, bookingData.description]);

  // Slot pre-selection
  useSlotPreSelection(
    searchParams,
    availableDays,
    bookingData,
    currentStep,
    eventType,
    (slot) => {
      const updatedData = { ...bookingData, selectedSlot: slot };
      updateBookingData({ selectedSlot: slot });
      updateURL(updatedData);

      setStep('booking-details');
    },
    setStep
  );

  // Handle slot selection
  const handleSlotSelect = useCallback(
    (slot: any) => {
      const updatedData = { ...bookingData, selectedSlot: slot };
      updateBookingData({ selectedSlot: slot });
      updateURL(updatedData);

      // Details is also the commit step, so it is the single next stop whether
      // or not name/email were prefilled from the URL.
      setStep('booking-details');
    },
    [bookingData, updateBookingData, updateURL, setStep]
  );

  // Handle booking submission
  const handleBookingSubmit = useCallback(async () => {
    setFieldErrors({});

    const validation = validateBookingData(bookingData, eventType);
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      const firstErrorField = Object.keys(validation.errors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!bookingData.selectedSlot) {
      setFieldErrors({ slot: 'Please select a time slot' });
      return;
    }

    const description = bookingData.description?.trim() || config.description || '';

    const submissionData: any = {
      email: bookingData.email.trim(),
      description: description,
      location: bookingData.isOnline ? '' : (bookingData.location || ''),
      isOnline: bookingData.isOnline ?? true,
      slot: {
        start: bookingData.selectedSlot.start,
        end: bookingData.selectedSlot.end,
      },
    };

    if (eventType) {
      submissionData.eventType = eventType;
    } else {
      submissionData.bookeeEmail = bookingData.bookeeEmail as any;
    }

    try {
      const result = await submitBooking(submissionData);

      if (result.success) {
        if (result.meetingDetails) {
          setMeetingDetails(result.meetingDetails);
        }
        setStep('success');
        setFieldErrors({});
      }
    } catch (error) {
      console.error('Booking submission failed:', error);
      setFieldErrors({ general: 'An unexpected error occurred. Please try again.' });
    }
  }, [bookingData, eventType, config.description, submitBooking, setStep]);

  // Handle navigation
  const handleGoBack = useCallback(() => {
    if (currentStep === 'select-slot' && !eventType) {
      setStep('select-person');
    } else if (currentStep === 'booking-details') {
      setStep('select-slot');
    }
  }, [currentStep, eventType, setStep]);

  const handleGoForward = useCallback(() => {
    if (currentStep === 'select-person' && bookingData.bookeeEmail) {
      setStep('select-slot');
    } else if (currentStep === 'select-slot' && bookingData.selectedSlot) {
      setStep('booking-details');
    } else if (currentStep === 'booking-details') {
      // Details is the commit step — forward books the meeting.
      handleBookingSubmit();
    }
  }, [currentStep, bookingData.bookeeEmail, bookingData.selectedSlot, setStep, handleBookingSubmit]);

  const handleBookAnother = useCallback(() => {
    setStep('select-person');
    setMeetingDetails(null);
    resetBookingData();
    router.replace(pathname || '');
  }, [setStep, resetBookingData, router, pathname]);

  // Get step info
  const stepInfo = useMemo(
    () => getStepInfo(currentStep, eventType, config.name),
    [currentStep, eventType, config.name]
  );

  const stepLabels = useMemo(() => getStepLabels(eventType), [eventType]);

  // Show error page if email validation failed
  if (emailValidationError) {
    return <EmailValidationError email={emailValidationError} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Full-width progress: bookers always see how many steps remain */}
        {currentStep !== 'success' && (
          <div className="mb-8">
            <ProgressIndicator stepInfo={stepInfo} stepLabels={stepLabels} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] gap-6 lg:gap-8 items-start">
          {/* Left pane: non-calendar context (meeting, duration, who, timezone, chosen time) */}
          {currentStep !== 'success' && (
            <aside className="lg:pt-2">
              <BookingSummarySidebar
                bookingData={bookingData}
                eventType={eventType}
                config={config}
                onTimeZoneChange={handleTimeZoneChange}
              />
            </aside>
          )}

          {/* Right pane: the active step (calendar / forms) */}
          <div className={currentStep === 'success' ? 'lg:col-span-2' : undefined}>
            <Card className="shadow-lg">
              <CardHeader className="pb-6">
                <CardTitle className="text-2xl font-bold text-card-foreground mb-2">
                  {stepInfo.title}
                </CardTitle>
                <p className="text-gray-600">{stepInfo.description}</p>
              </CardHeader>

              {/* Navigation - moved to top */}
              {currentStep !== 'success' && (
                <div className="px-6 pb-4 border-b border-gray-200">
                  <div className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={handleGoBack}
                      disabled={!canNavigateBackward(currentStep, eventType)}
                      className="flex items-center space-x-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back</span>
                    </Button>

                    <Button
                      onClick={handleGoForward}
                      disabled={!canNavigateForward(currentStep, bookingData, eventType) || isSubmitting}
                      className={`flex items-center space-x-2 ${
                        currentStep === 'booking-details'
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : ''
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Processing...</span>
                        </>
                      ) : currentStep === 'booking-details' ? (
                        <>
                          <span>Confirm booking</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          <span>Continue</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <CardContent className="px-6 pb-6">
                {/* Step 1: Select Person */}
                {currentStep === 'select-person' && !eventType && (
                  <SelectPersonStep bookingData={bookingData} onBookingDataChange={updateBookingData} />
                )}

                {/* Step 2: Select Slot */}
                {currentStep === 'select-slot' && (
                  <SelectSlotStep
                    bookingData={bookingData}
                    isLoading={isLoadingAvailability}
                    error={availabilityError}
                    availableDays={availableDays}
                    visibleMonth={visibleMonth}
                    minMonth={minMonth}
                    maxMonth={maxMonth}
                    onVisibleMonthChange={handleVisibleMonthChange}
                    onSlotSelect={handleSlotSelect}
                    onRetry={refetch}
                  />
                )}

                {/* Step 3: Booking Details */}
                {currentStep === 'booking-details' && (
                  <BookingDetailsStep
                    bookingData={bookingData}
                    fieldErrors={fieldErrors}
                    submissionError={submissionError}
                    eventTypeDescription={config.description}
                    onBookingDataChange={updateBookingData}
                    onFieldErrorClear={(field) => {
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors[field];
                        return newErrors;
                      });
                    }}
                  />
                )}

                {/* Final step: Success */}
                {currentStep === 'success' && (
                  <SuccessStep bookingData={bookingData} meetingDetails={meetingDetails} onBookAnother={handleBookAnother} />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
